import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Box,
  Chip,
  Grid,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  HelpOutline,
  Flag,
  FiberManualRecord,
  ErrorOutlineRounded,
  CloudDownload,
  TrackChanges,
  CheckCircle,
  Cancel,
  Downloading,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import LoadingScreen from "components/Loading";
import { getMyScorecard } from "services/api";
import { useAuth } from "context/authContext";
import {
  updateApprovementStatus,
  updateSelfAchievement,
  addEvidence,
  downloadAllEvidencesZip,
  getPerformanceComments,
} from "services/api";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import GaugeChart from "react-gauge-chart";
import { calculateKpiResult } from "services/utils";
import { getScorecardExport } from "services/api";
import { getOrCreateChatRoom } from "services/api";
import { updatePerformance } from "services/api";
import { fieldStyles } from "services/utils";
import ExportButton from "components/UserExportButton";
import MonthYearSelector from "components/DateSelector";
import SnackbarWrapper from "components/Snackbar";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import EvidenceDownloadButton from "components/ReusableEvidenceDownload";
import {
  getMyQuarterlyScorecard,
  addAdminPerformanceComment,
  resetPerformanceAchievement,
} from "services/api";
import CommentButton from "components/CommentButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmationDialog from "components/ConfirmationDialog";

const perspectives = [
  "Strategic Initiatives",
  "Strategic Projects",
  "Finance Viability",
  "Internal Process",
  "Internal StakeHolder",
  "External StakeHolder",
  "Compliance",
  "Audit",
];

const getTrendIcon = (trend) => {
  switch (trend) {
    case "up":
      return <TrendingUp color="success" />;
    case "down":
      return <TrendingDown color="error" />;
    case "flat":
      return <TrendingFlat color="warning" />;
    default:
      return <ErrorOutlineRounded color="disabled" />;
  }
};

const getResultColor = (finalResult, ach) => {
  if (ach == null) return "grey";
  if (finalResult > 105) return "blue";
  if (finalResult >= 90 && finalResult <= 105) return "green";
  if (finalResult >= 80 && finalResult < 90) return "yellow";
  if (finalResult >= 70 && finalResult < 80) return "orange";
  return "red";
};

const normalizeScore = (score) => {
  if (score <= 70) {
    return (score / 70) * 0.2; // Map 0-70 to 0%-20%
  } else if (score <= 80) {
    return 0.2 + ((score - 70) / 10) * 0.2; // Map 70-80 to 20%-40%
  } else if (score <= 90) {
    return 0.4 + ((score - 80) / 10) * 0.2; // Map 80-90 to 40%-60%
  } else if (score <= 105) {
    return 0.6 + ((score - 90) / 15) * 0.2; // Map 90-105 to 60%-80%
  } else {
    return 0.8 + ((score - 105) / 15) * 0.2; // Map 105-120 to 80%-100%
  }
};

const MergedView = ({ employee, isMe, isAdminView }) => {
  const { t, i18n } = useTranslation();
  const {
    setChatsChanged,
    chatschanged,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    quarter,
    isQuarterly,
    employee: me,
  } = useAuth(); //selectedYear,setSelectedYear

  const snackbarRef = useRef();

  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState(null);
  const [kpiData, setKpiData] = useState();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPerspective, setSelectedPerspective] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalResult, setTotalResult] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [rejectTargetLoading, setRejectTargetLoading] = useState(false);
  const [rejectAchLoading, setRejectAchLoading] = useState(false);
  const [filteredKpis, setFilteredKpis] = useState([]);
  const [isEditing, setIsEditing] = useState(null);
  const [selfAchievementData, setSelfAchievement] = useState([]);
  const [isCeo, setIsCeo] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState(null);
  const [targetInputValue, setTargetInputValue] = useState("");
  const [editingAchievementId, setEditingAchievementId] = useState(null);
  const [achievementInputValue, setAchievementInputValue] = useState("");
  const [didCancelEdit, setDidCancelEdit] = useState(false);
  const [didCancelEditTarget, setDidCancelEditTarget] = useState(false);
  const [didCancelEditAch, setDidCancelEditAch] = useState(false);
  const [performanceData, setPerformanceData] = useState([]);
  const [myId, setMyId] = useState(null);
  const [performanceComments, setPerformanceComments] = useState([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [kpiToReset, setKpiToReset] = useState(null);

  useEffect(() => {
    if (me?.id) {
      setMyId(me.id);
    }
  }, [me]);

  useEffect(() => {
    const fetchData = async () => {
      if (!loading) setLoading(true);
      const empRole = employee.role;
      if (empRole === 1) setIsCeo(true);

      await getData(employee);
      if (isAdminView) {
        const commentsData = await getPerformanceComments(
          employee.id,
          me.id,
          selectedMonth,
          selectedYear
        );

        if (commentsData !== "error") {
          setPerformanceComments(commentsData);
        } else {
          setPerformanceComments([]); // Reset on error
        }
      }
    };

    fetchData().then((e) => {
      setLoading(false);
    });
  }, [selectedMonth, selectedYear, employee, isQuarterly, quarter]);

  useEffect(() => {
    let timer;

    if (didCancelEditTarget || didCancelEditAch || didCancelEdit) {
      timer = setTimeout(() => {
        if (didCancelEditTarget) setDidCancelEditTarget(false);
        if (didCancelEditAch) setDidCancelEditAch(false);
        if (didCancelEdit) setDidCancelEdit(false);
      }, 100);
    }

    return () => clearTimeout(timer);
  }, [didCancelEditTarget, didCancelEditAch, didCancelEdit]);

  const getData = async (employee) => {
    let data;
    try {
      if (isQuarterly) {
        data = await getMyQuarterlyScorecard(employee.id, quarter, selectedYear);
      } else {
        data = await getMyScorecard(employee.id, employee.jobId, selectedMonth, selectedYear);
        console.log("Data::", data);
      }
      if (!data || data === "error") return;

      const mappedData = data.map((kpi) => ({
        performanceId: kpi.performanceId,
        kpiName: kpi.kpiName,
      }));
      setPerformanceData(mappedData);

      // data is now an array of objects, not strings.
      // We only need to transform fields that require specific UI logic (like trend arrows).
      const transformedData = data.map((entry) => {
        return {
          ...entry, // This spreads all fields from the DTO (kpiName, weight, etc.)

          // Match your specific UI field names to the DTO field names
          target: entry.kpiTarget,
          achievement: entry.kpiAchievement,
          kpiAssignmentId: entry.kpiAssignmentsId,

          // UI logic for trend
          trend: entry.trend ? (entry.trend > 0 ? "up" : "down") : null,

          // Ensure strings are handled for filenames
          fileName: entry.fileName || null,
          assessorFileName: entry.assessorFileName || null,

          // Perspective Name mapping
          type: entry.frequency,
        };
      });

      // Update States
      setTotalResult(calculateTotalWeightedResult(transformedData));
      setKpiData(transformedData);

      // Corrected the self-achievement mapping
      const selfAchList = transformedData.map((kpi) => kpi.selfAchievement);
      setSelfAchievement(selfAchList);

      // Filtering Logic
      const filteredKpiss = transformedData?.filter((kpi) => {
        const matchesSearch = kpi.kpiName.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPerspective = selectedPerspective
          ? kpi.perspectiveId - 1 === parseInt(selectedPerspective)
          : true;
        return matchesSearch && matchesPerspective;
      });

      setFilteredKpis(filteredKpiss);
    } catch (err) {
      console.error("Error fetching scorecard:", err);
    }
  };

  const handleAdminCommentSave = async (kpi, newComment) => {
    const commentPayload = {
      performanceId: kpi.performanceId,
      comment: newComment,
      commenterUuid: myId, // Using me.id as requested
    };

    const result = await addAdminPerformanceComment(commentPayload);

    if (result !== "error") {
      setPerformanceComments((prev) => {
        // Check if this comment is already in the array
        const exists = prev.find((c) => c.id === result.id);

        if (exists) {
          // UPDATE: Replace the old version with the new one from the server
          return prev.map((c) => (c.id === result.id ? result : c));
        } else {
          // INSERT: Add the new DTO to the list
          return [...prev, result];
        }
      });
    }
  };

  const handleAchievementReset = async () => {
    // Use the state variable instead of a parameter
    const kpi = kpiToReset;

    if (!kpi || !kpi.performanceId) {
      console.error("No performance record exists for this KPI yet.");
      setResetDialogOpen(false);
      return;
    }

    const result = await resetPerformanceAchievement(kpi.performanceId);

    if (result !== "error") {
      await getData(employee);

      setPerformanceComments((prev) => prev.filter((c) => c.performanceId !== kpi.performanceId));

      setResetDialogOpen(false);
      setKpiToReset(null);
    }
  };

  const handleExpandClick = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const handleEditClick = (performanceId, currentValue) => {
    setIsEditing(performanceId ?? "new"); // 'new' for creation case
    setSelfAchievement(currentValue ?? "");
  };

  useEffect(() => {
    if (kpiData && kpiData.length > 0) {
      setTotalResult(calculateTotalWeightedResult(kpiData));
    }
  }, [kpiData]);

  const openResetConfirmation = (kpi) => {
    // 1. "Remember" which KPI was clicked by saving it to state
    setKpiToReset(kpi);

    // 2. Open the Dialog component
    setResetDialogOpen(true);
  };

  const handleFileAttach = async (event, kpi, isSelfAchievement) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 20 * 1024 * 1024; // 20 MB
    if (file.size > maxSize) {
      snackbarRef.current?.showSnackbar(t("user.myScorecard.evidence.FileSizeLimit"), "error");
      event.target.value = null; // Reset file input
      return;
    }

    try {
      let result = null;

      // CASE 1: performanceId exists — directly upload the evidence
      if (kpi.performanceId) {
        result = await addEvidence(kpi.performanceId, file, isSelfAchievement);
      } else {
        // CASE 2: performanceId does not exist — create performance first

        let monthToSend = selectedMonth;
        if (isQuarterly) {
          // monthToSend = { Q1: 3, Q2: 6, Q3: 9, Q4: 12 }[quarter] ?? null;
          const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
          monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
        }

        const updateResult = await updatePerformance(
          employee.id,
          kpi.kpiAssignmentId,
          // selectedMonth,
          monthToSend,
          selectedYear,
          kpi.target
        );

        if (updateResult !== "error") {
          const newPerformanceId = updateResult.performanceId;

          // Now upload the evidence with the new performanceId
          result = await addEvidence(newPerformanceId, file, isSelfAchievement);

          // Update frontend state to reflect new performanceId
          setKpiData((prevData) => {
            const updated = prevData.map((item) =>
              item.kpiAssignmentId === kpi.kpiAssignmentId
                ? {
                    ...item,
                    performanceId: newPerformanceId,
                    fileName: file.name,
                  }
                : item
            );
            setFilteredKpis(updated);
            return updated;
          });
        } else {
          snackbarRef.current?.showSnackbar(
            t("user.myScorecard.evidence.CreatePerformanceError"),
            "error"
          );
          return;
        }
      }

      // Show success snackbar if file upload succeeded
      if (result) {
        snackbarRef.current?.showSnackbar(
          t("user.myScorecard.evidence.EvidenceUploadSuccess"),
          "success"
        );

        // Update filename in UI
        // 1. Determine which field name to update
        const fileNameField = isSelfAchievement ? "fileName" : "assessorFileName";

        setKpiData((prevData) => {
          const updated = prevData.map((item) =>
            // 2. Find the correct KPI by performanceId
            item.performanceId === (kpi.performanceId || result.performanceId)
              ? {
                  ...item,
                  // 3. Use the computed property name to update either fileName or assessorFileName
                  [fileNameField]: file.name,
                }
              : item
          );

          // Keep the filtered list in sync
          setFilteredKpis(updated);
          return updated;
        });
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        t("user.myScorecard.evidence.EvidenceUploadFailure"),
        "error"
      );
    }

    // Clear file input to allow re-uploading same file
    event.target.value = null;
  };

  const handleTargetOrAchievementUpdate = async (e, kpi, index) => {
    if ((index === 1 && didCancelEditTarget) || (index === 2 && didCancelEditAch)) return;
    // if (didCancelEditTarget) return;
    if (e.type === "blur" || e.key === "Enter") {
      const newValue = parseFloat(e.target.value);

      if (!isNaN(newValue)) {
        const originalValue =
          index === 1 ? parseFloat(kpi.target ?? "") : parseFloat(kpi.achievement ?? "");
        if (newValue === originalValue) {
          if (index === 1) setEditingTargetId(null);
          if (index === 2) setEditingAchievementId(null);
          return;
        }

        const targetValue = index === 1 ? newValue : kpi.target;
        const achievementValue = index === 2 ? newValue : kpi.achievement;
        // Call your updatePerformance API to update the target
        /////Modified
        let monthToSend = selectedMonth;
        if (isQuarterly) {
          const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
          monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
        }
        //////Modified
        const updateResult = await updatePerformance(
          employee.id,
          kpi.kpiAssignmentId,
          // selectedMonth,
          monthToSend,
          selectedYear,
          targetValue, // new target
          achievementValue, // keep existing achievement
          true // some flag, keep your existing logic
        );

        if (updateResult !== "error") {
          setKpiData((prevData) => {
            const updated = prevData.map((item) =>
              item.kpiAssignmentId === kpi.kpiAssignmentId
                ? {
                    ...item,
                    target: targetValue,
                    achievement: achievementValue,
                    performanceId: updateResult.performanceId || item.performanceId,
                  }
                : item
            );

            return updated;
          });

          // 2. Then filter separately using useEffect or right after if you have access to updated data
          const updatedFiltered = kpiData
            .map((item) =>
              item.kpiAssignmentId === kpi.kpiAssignmentId
                ? {
                    ...item,
                    target: targetValue,
                    achievement: achievementValue,
                    performanceId: updateResult.performanceId || item.performanceId,
                  }
                : item
            )
            .filter(
              (item) =>
                item.kpiName.toLowerCase().includes(searchQuery.toLowerCase()) &&
                (selectedPerspective
                  ? item.perspectiveId - 1 === parseInt(selectedPerspective)
                  : true)
            );

          setFilteredKpis(updatedFiltered);
          if (index === 1) {
            // showSnackbar(t("user.myScorecard.snackbar.targetUpdated"));
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.targetUpdated"),
              "success"
            );
          }
          if (index === 2) {
            // showSnackbar(t("user.myScorecard.snackbar.achievementUpdated"));
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.achievementUpdated"),
              "success"
            );
          }
        } else {
          if (index === 1) {
            // showSnackbar(t("user.myScorecard.snackbar.targetUpdateFailed"));
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.targetUpdateFailed"),
              "error"
            );
          }
          if (index === 2) {
            // showSnackbar(t("user.myScorecard.snackbar.achievementFailed"));
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.achievementFailed"),
              "error"
            );
          }
        }
      }
      // setEditingTargetId(null);  // stop editing after update or blur
      if (index === 1) setEditingTargetId(null);
      if (index === 2) setEditingAchievementId(null);
    }
  };

  const handleBlurOrEnter = async (e, kpi) => {
    if (didCancelEdit) return;

    if (e.type === "blur" || e.key === "Enter") {
      const newValue = parseFloat(e.target.value);
      const originalValue = parseFloat(kpi.selfAchievement ?? "");
      if (!isNaN(newValue) && newValue !== originalValue) {
        let updateResult = null;

        if (kpi.performanceId) {
          updateResult = await updateSelfAchievement(kpi.performanceId, newValue);
          if (updateResult !== "error") {
            setKpiData((prevData) => {
              const updated = prevData.map((item) =>
                item.performanceId === kpi.performanceId
                  ? { ...item, selfAchievement: newValue }
                  : item
              );

              // Re-filter using updated
              const reFiltered = updated.filter(
                (item) =>
                  item.kpiName.toLowerCase().includes(searchQuery.toLowerCase()) &&
                  (selectedPerspective
                    ? item.perspectiveId - 1 === parseInt(selectedPerspective)
                    : true)
              );
              setFilteredKpis(reFiltered);

              return updated;
            });

            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.selfAchievementUpdated"),
              "success"
            );
          } else {
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.selfAchievementUpdateFailed"),
              "error"
            );
          }
        } else {
          let monthToSend = selectedMonth;
          if (isQuarterly) {
            // monthToSend = { Q1: 3, Q2: 6, Q3: 9, Q4: 12 }[quarter] ?? null;
            const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
            monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
          }

          updateResult = await updatePerformance(
            employee.id,
            kpi.kpiAssignmentId,
            // selectedMonth,
            monthToSend,
            selectedYear,
            kpi.target,
            kpi.achievement,
            true,
            newValue
          );

          if (updateResult !== "error") {
            setKpiData((prevData) => {
              const updated = prevData.map((item) =>
                item.kpiAssignmentId === kpi.kpiAssignmentId
                  ? {
                      ...item,
                      selfAchievement: newValue,
                      performanceId: updateResult.performanceId,
                    }
                  : item
              );

              setFilteredKpis(updated);

              return updated;
            });
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.selfAchievementUpdated"),
              "success"
            );
          } else {
            snackbarRef.current?.showSnackbar(
              t("user.myScorecard.snackbar.selfAchievementUpdateFailed"),
              "error"
            );
          }
        }

        setIsEditing(null);
      } else {
        setIsEditing(null);
      }
    }
  };

  const handleApproveTarget = async (id, status) => {
    setRejectTargetLoading(true);
    const updatedKpi = kpiData.find((kpi) => kpi.kpiAssignmentId === id);
    if (!updatedKpi) return;
    const value = status === true ? (updatedKpi.approvedTarget === 2 ? 3 : 1) : 2;
    //employeeId, kpiId, month, year, value, isTarget
    let monthToSend = selectedMonth;
    if (isQuarterly) {
      const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
      monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
    }

    const res = await updateApprovementStatus(
      employee.id,
      id,
      // selectedMonth,
      monthToSend,
      selectedYear,
      value,
      true
    );
    if (res != "error") {
      setKpiData((prevData) => {
        const updated = prevData.map((kpi) => {
          if (kpi.kpiAssignmentId === id) {
            return {
              ...kpi,
              approvedTarget: status === true ? (kpi.approvedTarget === 2 ? 3 : 1) : 2,
            };
          }
          return kpi;
        });

        setFilteredKpis(updated);
        return updated;
      });
      if (status === false) {
        const chatroom = await getOrCreateChatRoom(updatedKpi.performanceId, employee.id, true);
        if (chatroom != "error") {
          const chat = {
            chatRoomId: chatroom.id,
            performanceId: chatroom.performanceId,
            unreadMessages: 0,
            kpiName: updatedKpi.kpiName,
            active: true,
          };
          navigate(`/chat/${chat.chatRoomId}`, { state: { chat } });
        }
      }
    }
    setChatsChanged(!chatschanged);

    setRejectTargetLoading(false);
  };

  const handleApproveAchievement = async (id, status) => {
    setRejectAchLoading(true);
    const updatedKpi = kpiData.find((kpi) => kpi.kpiAssignmentId === id);
    if (!updatedKpi) return;
    const value = status === true ? (updatedKpi.approvedAchievement === 2 ? 3 : 1) : 2;
    //employeeId, kpiId, month, year, value, isTarget
    let monthToSend = selectedMonth;
    if (isQuarterly) {
      const mappedMonth = { 1: 3, 2: 6, 3: 9, 4: 12 }[quarter];
      monthToSend = Number.isInteger(mappedMonth) ? mappedMonth : selectedMonth;
    }

    const res = await updateApprovementStatus(
      employee.id,
      id,
      // selectedMonth,
      monthToSend,
      selectedYear,
      value,
      false
    );
    if (res != "error") {
      setKpiData((prevData) => {
        const updated = prevData.map((kpi) => {
          if (kpi.kpiAssignmentId === id) {
            return {
              ...kpi,
              approvedAchievement: status === true ? (kpi.approvedAchievement === 2 ? 3 : 1) : 2,
            };
          }
          return kpi;
        });
        setFilteredKpis(updated);
        return updated;
      });

      if (status === false) {
        const chatroom = await getOrCreateChatRoom(updatedKpi.performanceId, employee.id, false);
        if (chatroom != "error") {
          const chat = {
            chatRoomId: chatroom.id,
            performanceId: chatroom.performanceId,
            unreadMessages: 0,
            kpiName: updatedKpi.kpiName,
          };
          navigate(`/chat/${chat.chatRoomId}`, { state: { chat } });
        }
        setChatsChanged(!chatschanged);
      }
    }

    setRejectAchLoading(false);
  };

  const calculateTotalWeightedResult = (transformedData) => {
    if (!transformedData || transformedData.length === 0) return 0;

    let totalWeightedPoints = 0;
    let sumOfActiveWeights = 0;

    transformedData.forEach((kpi) => {
      // 1. Validation check (Skip KPIs with no data or 0 target)
      if (
        kpi.target === null ||
        kpi.target === 0 ||
        kpi.weight === null ||
        (kpi.achievement === null && kpi.secondaryAchievement === null)
      ) {
        return;
      }

      const primaryPercentage = kpi.primaryPercentage !== null ? kpi.primaryPercentage : 100;
      const secondaryPercentage = 100 - primaryPercentage;

      // 2. Calculate primary component
      const primaryComponent =
        kpi.achievement !== null
          ? calculateKpiComponent(
              kpi.target,
              kpi.achievement,
              kpi.kpiFormula,
              kpi.weight,
              primaryPercentage / 100
            )
          : 0;

      // 3. Calculate secondary component
      const secondaryComponent =
        kpi.secondaryAchievement !== null
          ? calculateKpiComponent(
              kpi.target,
              kpi.secondaryAchievement,
              kpi.kpiFormula,
              kpi.weight,
              secondaryPercentage / 100
            )
          : 0;

      // 4. Accumulate the weighted points and the weight itself for normalization
      totalWeightedPoints += primaryComponent + secondaryComponent;
      sumOfActiveWeights += kpi.weight;
    });

    // 5. Normalization Step
    if (sumOfActiveWeights === 0) return "0.00";

    // Normalization math: (Sum of weighted points / Sum of weights)
    // Example: If points are 54 and total weight is 60, (54/60) = 0.9.
    // Then multiply by 100 to get 90.00%
    const normalizedResult = (totalWeightedPoints / sumOfActiveWeights) * 100;

    return normalizedResult.toFixed(2);
  };

  // Helper remains the same, but notice it returns "weighted points"
  const calculateKpiComponent = (target, achievement, formula, weight, percentage) => {
    if (target === 0) return 0;

    const formulaType = formula?.toLowerCase().trim();
    let result;

    if (formulaType === "regular") {
      result = ((achievement - target) / target + 1) * weight * percentage;
    } else if (formulaType === "reverse") {
      result = ((target - achievement) / target + 1) * weight * percentage;
    } else {
      result = 0;
    }

    return result;
  };

  const handleDownload = async () => {
    setDownloadLoading(true); // 🔹 start loader immediately

    try {
      const isMonthly = !isQuarterly;
      const value = isMonthly ? selectedMonth : quarter;

      // 🔹 Download Excel
      await getScorecardExport(employee.id, value, selectedYear, employee.name, isMonthly);

      // 🔹 Download ZIP of all evidences
      if (performanceData && performanceData.length > 0) {
        await downloadAllEvidencesZip(performanceData, employee.name, value, selectedYear);
      }
    } catch (err) {
      console.error("Download failed", err);
    } finally {
      setDownloadLoading(false); // 🔹 stop loader after both downloads
    }
  };

  const memoizedKpiResults = useMemo(() => {
    const filtered = filteredKpis.filter((kpi) => {
      const matchesPerspective =
        selectedPerspective === "" || kpi.perspectiveId - 1 === selectedPerspective;

      const matchesSearch =
        searchQuery.trim() === "" ||
        kpi.kpiName.toLowerCase().includes(searchQuery.trim().toLowerCase());

      return matchesPerspective && matchesSearch;
    });
    return filtered.map((kpi) => {
      let finalResult = calculateKpiResult(
        kpi.target,
        kpi.achievement,
        kpi.secondaryAchievement,
        kpi.kpiFormula,
        kpi.primaryPercentage
      );
      finalResult = finalResult === Infinity || finalResult === -Infinity ? 0 : finalResult;

      let weightedResult = calculateKpiResult(
        kpi.target,
        kpi.achievement,
        kpi.secondaryAchievement,
        kpi.kpiFormula,
        kpi.primaryPercentage,
        kpi.weight
      );
      weightedResult =
        weightedResult === Infinity || weightedResult === -Infinity ? 0 : weightedResult;

      const resultColor = getResultColor(finalResult, kpi.achievement);

      return {
        kpi,
        finalResult,
        weightedResult,
        resultColor,
      };
    });
  }, [filteredKpis, selectedPerspective, searchQuery]);

  const memoizedGaugeChart = useMemo(() => {
    return (
      <GaugeChart
        id="score-gauge"
        nrOfLevels={30}
        arcWidth={0.2}
        percent={normalizeScore(totalResult)}
        colors={["#FF5F00", "#FFDD00", "#00B100"]}
        hideText={true}
        animate={false}
        style={{ width: "200px" }}
        needleColor="#333"
        needleBaseColor="#333"
      />
    );
  }, [totalResult]);

  if (loading) {
    return <LoadingScreen />;
  }
  return (
    <Box>
      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />

      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">
            {isMe
              ? t("user.myScorecard.title.my")
              : t("user.myScorecard.title.other", { name: employee.name })}
          </Typography>

          <ExportButton
            onClick={handleDownload}
            loading={downloadLoading}
            label={t("user.myScorecard.export")}
          />
        </Box>
        <Grid container spacing={1} sx={{ mb: 1, alignItems: "center" }}>
          {/* KPI Search */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("user.myScorecard.searchKpi")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={fieldStyles}
            />
          </Grid>

          {/* Perspective */}
          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("user.myScorecard.perspective")}
              value={selectedPerspective}
              sx={fieldStyles}
              onChange={(e) => setSelectedPerspective(e.target.value)}
            >
              <MenuItem value="">{t("user.myScorecard.allPerspectives")}</MenuItem>
              {perspectives.map((persp, index) => (
                <MenuItem key={index} value={index}>
                  {/* {persp} */}
                  {t(`user.assess.perspectives.${persp}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {!loading && totalResult != null && kpiData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%", // Ensures it stays centered
              mb: 1,
              position: "relative",
            }}
          >
            {memoizedGaugeChart}
            <Box
              sx={{
                position: "absolute",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "18px",
              }}
            >
              {totalResult}%
            </Box>
          </Box>
        )}
        {!loading && memoizedKpiResults ? (
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Box
              sx={{
                maxHeight: 400, // Adjust height as needed
                overflowY: "auto",
              }}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell>
                      <b>{t("user.myScorecard.kpiName")}</b>
                    </TableCell>
                    <TableCell align="center">
                      <b>{t("user.myScorecard.status")}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{t("user.myScorecard.result")}</b>
                    </TableCell>
                  </TableRow>

                  {memoizedKpiResults.map(({ kpi, finalResult, weightedResult, resultColor }) => {
                    const isMatched =
                      (kpi.type === "Quarterly" && isQuarterly) ||
                      (kpi.type === "Monthly" && !isQuarterly);
                    return (
                      <Fragment key={kpi.kpiAssignmentId}>
                        <TableRow
                          hover
                          sx={{ "&:hover": { cursor: "pointer" } }}
                          onClick={() => handleExpandClick(kpi.kpiAssignmentId)}
                        >
                          <TableCell>
                            <Typography variant="subtitle1">{kpi.kpiName}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                              {isMe &&
                                !isCeo &&
                                (kpi.approvedTarget === 0 || kpi.approvedTarget === 2) && (
                                  <Tooltip title={t("user.myScorecard.targetPending")}>
                                    <TrackChanges color="info" />
                                  </Tooltip>
                                )}
                              {isMe &&
                                !isCeo &&
                                kpi.achievement != null &&
                                (kpi.approvedAchievement === 0 ||
                                  kpi.approvedAchievement === 2) && (
                                  <Tooltip title={t("user.myScorecard.achievementPending")}>
                                    <Flag color="secondary" />
                                  </Tooltip>
                                )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 1,
                              }}
                            >
                              <FiberManualRecord sx={{ color: resultColor, fontSize: "1rem" }} />
                              {kpi.achievement != null && (
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {finalResult?.toFixed(2)}%
                                </Typography>
                              )}
                              {getTrendIcon(kpi.trend)}
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Details */}
                        <TableRow>
                          <TableCell colSpan={3} sx={{ p: 0, bgcolor: "#f9f9f9" }}>
                            <Collapse
                              in={expandedCard === kpi.kpiAssignmentId}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                  {/* Keep all your existing collapse content here */}
                                  <Grid item xs={12} md={6}>
                                    <Typography>
                                      <b>{t("user.myScorecard.perspective")}:</b>{" "}
                                      {/* {perspectives[kpi.perspectiveId - 1]} */}
                                      {t(
                                        `user.assess.perspectives.${
                                          perspectives[kpi.perspectiveId - 1]
                                        }`
                                      )}
                                    </Typography>
                                    <Typography>
                                      <b>{t("user.myScorecard.weight")}:</b> {kpi.weight}%
                                    </Typography>
                                    <Typography>
                                      <b>{t("user.myScorecard.formula")}:</b>{" "}
                                      {kpi.kpiFormula == "Regular"
                                        ? t("user.myScorecard.regular")
                                        : t("user.myScorecard.reverse")}
                                    </Typography>

                                    {!isCeo && (
                                      <>
                                        <Typography>
                                          <b>{t("admin.scorecard.dialog.primaryPercentage")}:</b>{" "}
                                          {kpi.primaryPercentage}%
                                        </Typography>
                                        <Typography>
                                          <b>{t("admin.scorecard.dialog.secondaryPercentage")}:</b>{" "}
                                          {100 - kpi.primaryPercentage}%
                                        </Typography>
                                      </>
                                    )}
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    {/* Target Section */}
                                    <Box sx={{ mb: 2 }}>
                                      {isCeo &&
                                      (!kpi.type || isMatched) &&
                                      editingTargetId === kpi.kpiAssignmentId ? (
                                        <Box display="flex" alignItems="center">
                                          <Typography mr={1}>
                                            <b>{t("user.assess.columns.Target")}:</b>
                                          </Typography>
                                          <TextField
                                            value={targetInputValue}
                                            onChange={(e) => setTargetInputValue(e.target.value)}
                                            onBlur={(e) =>
                                              handleTargetOrAchievementUpdate(e, kpi, 1)
                                            }
                                            onKeyDown={(e) =>
                                              e.key === "Enter" &&
                                              handleTargetOrAchievementUpdate(e, kpi, 1)
                                            }
                                            autoFocus
                                            size="small"
                                            variant="outlined"
                                            sx={{ width: 100 }}
                                          />
                                          <IconButton
                                            onMouseDown={() => {
                                              setDidCancelEditTarget(true); // Set cancel flag early enough
                                            }}
                                            onClick={() => {
                                              setEditingTargetId(null); // Exit edit mode
                                              setTargetInputValue(""); // Reset input
                                            }}
                                            size="small"
                                            sx={{ ml: 1 }}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ) : (
                                        <Box display="flex" alignItems="center">
                                          <Typography>
                                            <b>{t("user.assess.columns.Target")}:</b> {kpi.target}
                                          </Typography>
                                          {isCeo && (!kpi.type || isMatched) && (
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setEditingTargetId(kpi.kpiAssignmentId);
                                                setTargetInputValue(kpi.target?.toString() || "");
                                              }}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      )}

                                      {/* Approve/Reject buttons */}
                                      {rejectTargetLoading ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        isMe &&
                                        (!kpi.type || isMatched) &&
                                        !isCeo &&
                                        (kpi.approvedTarget === 0 || kpi.approvedTarget === 2) && (
                                          <Box sx={{ mt: 1 }}>
                                            <MDButton
                                              sx={{ mr: 1, borderRadius: 16 }}
                                              variant="outlined"
                                              color="success"
                                              size="small"
                                              startIcon={<CheckCircle />}
                                              onClick={() =>
                                                handleApproveTarget(kpi.kpiAssignmentId, true)
                                              }
                                            >
                                              {t("user.myScorecard.accept")}
                                            </MDButton>
                                            {kpi.approvedTarget !== 2 && (
                                              <MDButton
                                                sx={{ borderRadius: 16 }}
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<Cancel />}
                                                onClick={() =>
                                                  handleApproveTarget(kpi.kpiAssignmentId, false)
                                                }
                                              >
                                                {t("user.myScorecard.reject")}
                                              </MDButton>
                                            )}
                                          </Box>
                                        )
                                      )}
                                    </Box>
                                    {/* Achievement Section */}
                                    <Box sx={{ mb: 2 }}>
                                      {isCeo &&
                                      (!kpi.type || isMatched) &&
                                      editingAchievementId === kpi.kpiAssignmentId ? (
                                        <Box display="flex" alignItems="center">
                                          <Typography mr={1}>
                                            <b>{t("user.assess.columns.Achievement")}:</b>
                                          </Typography>
                                          <TextField
                                            value={achievementInputValue}
                                            onChange={(e) =>
                                              setAchievementInputValue(e.target.value)
                                            }
                                            onBlur={(e) =>
                                              handleTargetOrAchievementUpdate(e, kpi, 2)
                                            }
                                            onKeyDown={(e) =>
                                              e.key === "Enter" &&
                                              handleTargetOrAchievementUpdate(e, kpi, 2)
                                            }
                                            autoFocus
                                            size="small"
                                            variant="outlined"
                                            sx={{ width: 100 }}
                                          />
                                          <IconButton
                                            onMouseDown={() => {
                                              setDidCancelEditAch(true);
                                            }}
                                            onClick={() => {
                                              setEditingAchievementId(null);
                                              setAchievementInputValue("");
                                            }}
                                            size="small"
                                            sx={{ ml: 1 }}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ) : (
                                        <Box display="flex" alignItems="center">
                                          <Typography>
                                            <b>{t("user.assess.columns.Achievement")}:</b>{" "}
                                            {kpi.achievement ?? ""}
                                          </Typography>

                                          {/* --- ASSESSOR EVIDENCE & COMMENT LOGIC START --- */}
                                          <Box
                                            sx={{
                                              ml: 1,
                                              display: "inline-flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <span>
                                              <EvidenceDownloadButton
                                                fileName={kpi.assessorFileName}
                                                performanceId={kpi.performanceId}
                                                isSelfEvidence={false}
                                                myScorecardView={true}
                                              />
                                            </span>

                                            {/* ✅ NEW COMMENT BUTTON ADDED HERE */}
                                            <Box sx={{ mx: 0.5 }}>
                                              <CommentButton
                                                item={kpi} // Mapped 'item' to 'kpi'
                                                isPrimary={false} // Mapped 'isPrimary' to 'isCeo' (or true)
                                                onSave={() => {}} // Does nothing as requested
                                                myScorecardView={true}
                                              />
                                            </Box>

                                            {isAdminView && kpi.achievement && (
                                              // <Box sx={{ mx: 0.5 }}>
                                              <Box
                                                sx={{
                                                  mx: 0.5,
                                                  display: "flex",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <CommentButton
                                                  item={kpi}
                                                  isPrimary={true} // Set to true to distinguish from the regular comment if needed
                                                  isAdminScorecardView={true}
                                                  currentUser={me}
                                                  allComments={performanceComments.filter(
                                                    (c) => c.performanceId === kpi.performanceId
                                                  )}
                                                  onSave={(newComment) =>
                                                    handleAdminCommentSave(kpi, newComment)
                                                  }
                                                />
                                                {/* 2. The New Reset Achievement Button */}
                                                {/* Only show if performanceId exists to ensure we have a record to reset */}
                                                {kpi.performanceId && (
                                                  <IconButton
                                                    size="small"
                                                    title={t("user.assess.resetAch")}
                                                    // onClick={() => handleAchievementReset(kpi)}
                                                    onClick={(e) => {
                                                      e.stopPropagation(); // Prevents the row from expanding/collapsing when clicking delete
                                                      openResetConfirmation(kpi);
                                                    }}
                                                    sx={{
                                                      ml: 1, // Add a little margin from the comment button
                                                      border: "1px solid #eee",
                                                      borderRadius: "8px",
                                                      color: "#ef4444", // Your red color
                                                      "&:hover": {
                                                        backgroundColor: "#fef2f2", // Light red background on hover
                                                        borderColor: "#fecaca",
                                                      },
                                                    }}
                                                  >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                  </IconButton>
                                                )}
                                              </Box>
                                            )}

                                            {isCeo && (
                                              <>
                                                <input
                                                  type="file"
                                                  id={`assessor-file-upload-${kpi.performanceId}`}
                                                  hidden
                                                  onChange={(e) => {
                                                    handleFileAttach(e, kpi, false);
                                                    e.target.value = null;
                                                  }}
                                                />
                                                <label
                                                  htmlFor={`assessor-file-upload-${kpi.performanceId}`}
                                                >
                                                  <Tooltip
                                                    title={
                                                      kpi.assessorFileName
                                                        ? t(
                                                            "user.myScorecard.evidence.ModifyEvidenceAttachment",
                                                            {
                                                              fileName: kpi.assessorFileName,
                                                            }
                                                          )
                                                        : t(
                                                            "user.myScorecard.evidence.AddEvidenceAttachment"
                                                          )
                                                    }
                                                    arrow
                                                  >
                                                    <IconButton
                                                      component="span"
                                                      size="small"
                                                      sx={{
                                                        color: kpi.assessorFileName
                                                          ? "green"
                                                          : "inherit",
                                                      }}
                                                    >
                                                      <AttachFileIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </label>
                                              </>
                                            )}
                                          </Box>
                                          {/* --- ASSESSOR EVIDENCE LOGIC END --- */}

                                          {isCeo && (!kpi.type || isMatched) && (
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setEditingAchievementId(kpi.kpiAssignmentId);
                                                setAchievementInputValue(
                                                  kpi.achievement?.toString() || ""
                                                );
                                              }}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      )}

                                      {/* Approve/Reject buttons */}
                                      {rejectAchLoading ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        isMe &&
                                        (!kpi.type || isMatched) &&
                                        !isCeo &&
                                        kpi.achievement != null &&
                                        (kpi.approvedAchievement === 0 ||
                                          kpi.approvedAchievement === 2) && (
                                          <Box sx={{ mt: 1 }}>
                                            <MDButton
                                              sx={{ mr: 1, borderRadius: 16 }}
                                              variant="outlined"
                                              color="success"
                                              size="small"
                                              startIcon={<CheckCircle />}
                                              onClick={() =>
                                                handleApproveAchievement(kpi.kpiAssignmentId, true)
                                              }
                                            >
                                              {t("user.myScorecard.accept")}
                                            </MDButton>
                                            {kpi.approvedAchievement !== 2 && (
                                              <MDButton
                                                sx={{ borderRadius: 16 }}
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<Cancel />}
                                                onClick={() =>
                                                  handleApproveAchievement(
                                                    kpi.kpiAssignmentId,
                                                    false
                                                  )
                                                }
                                              >
                                                {t("user.myScorecard.reject")}
                                              </MDButton>
                                            )}
                                          </Box>
                                        )
                                      )}
                                    </Box>
                                    {/* Self Achievement Section */}
                                    {!isCeo && (!kpi.type || isMatched) && (
                                      <Box sx={{ mb: 2 }}>
                                        {isEditing === (kpi.performanceId ?? "new") ? (
                                          <Box display="flex" alignItems="center">
                                            <Typography mr={1}>
                                              <b>{t("user.assess.columns.selfAch")}:</b>
                                            </Typography>
                                            <TextField
                                              value={selfAchievementData}
                                              onChange={(e) => setSelfAchievement(e.target.value)}
                                              onBlur={(e) => handleBlurOrEnter(e, kpi)}
                                              onKeyDown={(e) =>
                                                e.key === "Enter" && handleBlurOrEnter(e, kpi)
                                              }
                                              autoFocus
                                              size="small"
                                              variant="outlined"
                                            />

                                            <IconButton
                                              onMouseDown={() => {
                                                setDidCancelEdit(true); // Set cancel flag early enough
                                              }}
                                              onClick={() => {
                                                setIsEditing(null); // Exit edit mode
                                                setSelfAchievement(""); // Reset input
                                              }}
                                              size="small"
                                              sx={{ ml: 1 }}
                                            >
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Box display="flex" alignItems="center">
                                            <Typography>
                                              <b>{t("user.assess.columns.selfAch")}:</b>{" "}
                                              {kpi.selfAchievement ?? ""}
                                            </Typography>

                                            {isMe && (
                                              <>
                                                <IconButton
                                                  onClick={() =>
                                                    handleEditClick(
                                                      kpi.performanceId,
                                                      kpi.selfAchievement
                                                    )
                                                  }
                                                  size="small"
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                              </>
                                            )}
                                            <span>
                                              <EvidenceDownloadButton
                                                fileName={kpi.fileName}
                                                performanceId={kpi.performanceId}
                                                isSelfEvidence={true}
                                                myScorecardView={true}
                                              />
                                            </span>
                                            {isMe && (
                                              <>
                                                {/* File input + label-triggered attach icon */}
                                                <input
                                                  type="file"
                                                  id={`file-upload-${kpi.performanceId}`}
                                                  hidden
                                                  onChange={(e) => {
                                                    handleFileAttach(e, kpi, true);
                                                    e.target.value = null; // allow reselecting same file
                                                  }}
                                                />
                                                <label htmlFor={`file-upload-${kpi.performanceId}`}>
                                                  {/* <Tooltip title="Add Evidence Attachment" arrow> */}

                                                  <Tooltip
                                                    title={
                                                      kpi.fileName
                                                        ? t(
                                                            "user.myScorecard.evidence.EvidenceLabel",
                                                            { fileName: kpi.fileName }
                                                          )
                                                        : t(
                                                            "user.myScorecard.evidence.AddEvidenceAttachment"
                                                          )
                                                    }
                                                    arrow
                                                  >
                                                    <IconButton
                                                      component="span"
                                                      size="small"
                                                      sx={{
                                                        color: kpi.fileName ? "green" : "inherit", // ✅ Green if file is uploaded
                                                      }}
                                                    >
                                                      <AttachFileIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                </label>
                                              </>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        ) : (
          <Box>
            <MDTypography variant="body1" color="textSecondary">
              {t("user.myScorecard.noPublishedKPIs")}
            </MDTypography>
          </Box>
        )}
      </Box>
      <ConfirmationDialog
        open={resetDialogOpen}
        onClose={() => {
          setResetDialogOpen(false);
          setKpiToReset(null);
        }}
        onConfirm={handleAchievementReset} // Calls the function above
      />
    </Box>
  );
};

MergedView.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    jobId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.number.isRequired,
  }).isRequired,
  isMe: PropTypes.bool.isRequired,
  month: PropTypes.number,
  year: PropTypes.number,
  isAdminView: PropTypes.bool,
};

export default MergedView;
